import React, { useState, useEffect } from "react";
import { Procurement } from "@/api/entities";
import { alertsApi } from "@/lib/api/alerts";
import { resolveEffectiveTenant } from "@/lib/tenantUtils";
import { useUser, useCurrentTenant } from "@/contexts/UserContext";
import { MomentumScore } from "@/api/entities";
import { PersonaActivity } from "@/api/entities";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { X, ExternalLink, TrendingUp, UserCheck, ShoppingCart, CheckCheck, Mail } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { motion } from "framer-motion";

const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'https://api.neogtm.com' 
  : 'https://api.neogtm.com';

const AlertIcon = ({ type }) => {
  const icons = {
    live_deal: <ShoppingCart className="w-4 h-4 text-blue-600" />,
    momentum: <TrendingUp className="w-4 h-4 text-green-600" />,
    persona: <UserCheck className="w-4 h-4 text-purple-600" />
  };
  return icons[type] || <div className="w-4 h-4 bg-gray-400 rounded-full" />;
};

const AlertItem = ({ alert, externalUrl, onRead }) => {
  const handleClick = async (e) => {
    // Mark as read when clicking
    await onRead(alert.id);
  };

  const isUnread = !alert.read_at;
  const metadata = alert.metadata || {};
  const hasExternalLink = externalUrl && externalUrl.trim() !== '';

  const content = (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex-shrink-0">
        <AlertIcon type={alert.type} />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <h4 className={`text-sm font-medium text-gray-900 ${isUnread ? 'font-semibold' : ''}`}>
              {alert.title}
            </h4>
            <p className="text-xs text-gray-600 mt-1">{alert.reason}</p>
          </div>
          
          <div className="flex-shrink-0 text-right">
            <p className="text-xs text-gray-500">
              {formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}
            </p>
            {isUnread && (
              <div className="w-2 h-2 bg-blue-600 rounded-full mt-1 ml-auto"></div>
            )}
          </div>
        </div>
        
        {/* Metadata chips */}
        {(metadata.category || metadata.value || metadata.confidence) && (
          <div className="flex flex-wrap gap-1 mt-2">
            {metadata.category && (
              <Badge variant="outline" className="text-xs">
                {metadata.category}
              </Badge>
            )}
            {metadata.value && (
              <Badge variant="outline" className="text-xs">
                {metadata.value}
              </Badge>
            )}
            {metadata.confidence && (
              <Badge 
                variant="outline" 
                className={`text-xs ${
                  metadata.confidence === 'H' ? 'text-green-700 border-green-200' :
                  metadata.confidence === 'M' ? 'text-yellow-700 border-yellow-200' :
                  'text-red-700 border-red-200'
                }`}
              >
                {metadata.confidence}
              </Badge>
            )}
            {alert.delta && (
              <Badge variant="outline" className="text-xs">
                Δ {alert.delta > 0 ? '+' : ''}{alert.delta}
              </Badge>
            )}
          </div>
        )}
      </div>
      
      {hasExternalLink && (
        <div className="mt-0.5 flex-shrink-0">
          <ExternalLink className="w-4 h-4 text-gray-400" />
        </div>
      )}
    </div>
  );

  return (
    <motion.div
      key={alert.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={`border-b border-gray-100 last:border-b-0 ${isUnread ? 'bg-blue-50' : 'bg-white'}`}
    >
      {hasExternalLink ? (
        <a
          href={externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          className="block w-full p-4 text-left hover:bg-gray-50 transition-colors cursor-pointer"
        >
          {content}
        </a>
      ) : (
        <div className="w-full p-4 cursor-default opacity-60">
          {content}
        </div>
      )}
    </motion.div>
  );
};

// Helper to check if a string looks like a valid database ID
function isValidId(str) {
  if (!str || typeof str !== 'string') return false;
  
  // MongoDB ObjectId (24 hex chars)
  if (/^[a-f0-9]{24}$/i.test(str)) return true;
  
  // UUID format
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str)) return true;
  
  // Names typically contain dots, underscores as separators
  if (str.includes('.') || str.includes('_')) return false;
  
  // Skip if it looks like an email
  if (str.includes('@')) return false;
  
  // Generic alphanumeric ID (at least 15 chars, no special chars)
  if (/^[a-zA-Z0-9]+$/.test(str) && str.length >= 15) return true;
  
  return false;
}

// Helper to batch process alerts and avoid rate limits
async function fetchUrlsInBatches(alerts, batchSize = 3, delayMs = 500) {
  const urls = {};
  
      // Only process alerts that:
      // 1. Don't already have source_url set
      // 2. Have valid-looking entity_ids
      // NOTE: Entity-layer calls (Procurement.filter, MomentumScore.filter, PersonaActivity.filter)
      // may not be fully tenant-safe. Alert list is tenant-scoped; linked URL enrichment is a
      // follow-up risk—test under admin tenant switching.
      const alertsNeedingUrls = alerts.filter(a => !a.source_url && a.entity_id && isValidId(a.entity_id));
  
  
  for (let i = 0; i < alertsNeedingUrls.length; i += batchSize) {
    const batch = alertsNeedingUrls.slice(i, i + batchSize);
    
    await Promise.all(
      batch.map(async (alert) => {
        try {
          let sourceUrl = null;
          
          // Fetch from appropriate entity
          if (alert.type === 'live_deal') {
            const procurements = await Procurement.filter({ id: alert.entity_id });
            if (procurements.length > 0) {
              sourceUrl = procurements[0].source_url;
            }
          } else if (alert.type === 'momentum') {
            const momentumScores = await MomentumScore.filter({ id: alert.entity_id });
            if (momentumScores.length > 0) {
              sourceUrl = momentumScores[0].source_url;
            }
          } else if (alert.type === 'persona') {
            const activities = await PersonaActivity.filter({ id: alert.entity_id });
            if (activities.length > 0) {
              sourceUrl = activities[0].source_url || activities[0].full_url;
            }
          }
          
          if (sourceUrl) {
            urls[alert.id] = sourceUrl;
          }
        } catch (error) {
          // Silently ignore errors - old alerts may have invalid references
        }
      })
    );
    
    // Add delay between batches to avoid rate limits
    if (i + batchSize < alertsNeedingUrls.length) {
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }
  
  // Also include alerts that already had source_url
  alerts.forEach(alert => {
    if (alert.source_url) {
      urls[alert.id] = alert.source_url;
    }
  });
  
  
  return urls;
}

export default function AlertsPane({ isOpen, onClose, onAlertsSeen, onAlertsRead }) {
  const contextUser = useUser();
  const currentTenant = useCurrentTenant();
  const tenantId = resolveEffectiveTenant(
    contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
    currentTenant,
    contextUser,
    'instructure'
  );
  const userId = contextUser?.id || null;

  const [alerts, setAlerts] = useState([]);
  const [alertUrls, setAlertUrls] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);
  const [outboxMessages, setOutboxMessages] = useState([]);
  const [outboxCount, setOutboxCount] = useState(0);

  useEffect(() => {
    if (isOpen) {
      loadAlerts();
    }
  }, [isOpen, userId, tenantId]);

  const loadOutboxCount = async (uid, tid) => {
    if (!uid || !tid) return;
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/outbox/new-count?tenant_id=${tid}&user_id=${uid}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setOutboxCount(data.count || 0);
      }
    } catch (e) {
      setOutboxCount(0);
    }
  };

  const loadOutboxMessages = async (uid, tid) => {
    if (!uid || !tid) return;
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/outbox/recent?tenant_id=${tid}&user_id=${uid}&limit=5`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setOutboxMessages(data.messages || []);
      } else {
        setOutboxMessages([]);
      }
    } catch (error) {
      setOutboxMessages([]);
    }
  };

  const loadAlerts = async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const [allAlerts] = await Promise.all([
        // Keep in sync with AlertBellTrigger count source (limit 100),
        // otherwise "mark all read" can leave unread items outside this page slice.
        alertsApi.list(userId, tenantId, 100),
        loadOutboxMessages(userId, tenantId),
        loadOutboxCount(userId, tenantId)
      ]);

      setAlerts(allAlerts);

      const urls = await fetchUrlsInBatches(allAlerts, 3, 500);
      setAlertUrls(urls);

      const unseenAlerts = allAlerts.filter(alert => !alert.seen_at);
      if (unseenAlerts.length > 0) {
        try {
          await alertsApi.markAllSeen(allAlerts, tenantId);
        } catch (e) {
          console.warn('🔔 AlertsPane: Could not mark alerts as seen:', e);
        }
        if (typeof onAlertsSeen === "function") {
          onAlertsSeen();
        }
      }
    } catch (error) {
      console.error("Error loading alerts:", error);
    }
    setIsLoading(false);
  };

  const handleMarkAllRead = async () => {
    setIsMarkingAllRead(true);
    try {
      console.log('📝 Marking alerts as read');
      
      await alertsApi.markAllRead(alerts, tenantId);
  
      const now = new Date().toISOString();
      setAlerts(prev => prev.map(alert => ({
        ...alert,
        read_at: alert.read_at || now
      })));
  
      if (typeof onAlertsRead === "function") {
        onAlertsRead();
      }
  
      window.dispatchEvent(new CustomEvent('alerts-updated'));
    } catch (error) {
      console.error("Error marking all alerts as read:", error);
    }
    setIsMarkingAllRead(false);
  };

  const handleMarkAllItemsRead = async () => {
    setIsMarkingAllRead(true);
    try {
      if (unreadAlerts.length > 0) {
        await alertsApi.markAllRead(alerts, tenantId);
        const now = new Date().toISOString();
        setAlerts(prev => prev.map(alert => ({
          ...alert,
          read_at: alert.read_at || now
        })));
        if (typeof onAlertsRead === "function") {
          onAlertsRead();
        }
      }

      if (outboxCount > 0) {
        await fetch(`${API_BASE_URL}/api/outbox/dismiss`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ tenant_id: tenantId, user_id: userId })
        });
        setOutboxMessages([]);
        setOutboxCount(0);
        window.dispatchEvent(new CustomEvent('outbox-updated'));
      }

      window.dispatchEvent(new CustomEvent('alerts-updated'));
    } catch (error) {
      console.error("Error marking all items as read:", error);
    }
    setIsMarkingAllRead(false);
  };

  const handleRead = async (alertId) => {
    try {
      const now = new Date().toISOString();
      await alertsApi.update(alertId, { read_at: now }, tenantId);
      
      console.log('✅ Marked alert as read:', alertId);
      
      setAlerts(prev => prev.map(alert => 
        alert.id === alertId 
          ? { ...alert, read_at: now }
          : alert
      ));
  
      if (typeof onAlertsRead === "function") {
        onAlertsRead();
      }
  
      window.dispatchEvent(new CustomEvent('alerts-updated'));
    } catch (error) {
      console.error("Error marking alert as read:", error);
    }
  };

  const handleDismissOutbox = async () => {
    try {
      if (!userId) return;

      const response = await fetch(`${API_BASE_URL}/api/outbox/dismiss`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tenant_id: tenantId, user_id: userId })
      });
      
      if (response.ok) {
        setOutboxMessages([]);
        setOutboxCount(0);
        window.dispatchEvent(new CustomEvent('outbox-updated'));
      }
    } catch (e) {
      console.error('Error dismissing outbox:', e);
    }
  };

  const handleViewOutboxMessage = async (messageId) => {
    try {
      if (!userId) return;

      const response = await fetch(`${API_BASE_URL}/api/outbox/${messageId}/mark-viewed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tenant_id: tenantId, user_id: userId })
      });

      if (response.ok) {
        setOutboxMessages(prev => prev.filter(message => message.id !== messageId));
        setOutboxCount(prev => Math.max(0, prev - 1));
        window.dispatchEvent(new CustomEvent('outbox-updated'));
      }
    } catch (error) {
      console.warn("Mark viewed endpoint not available yet");
    }
  };

  const unreadAlerts = alerts.filter(alert => !alert.read_at);
  const readAlerts = alerts.filter(alert => alert.read_at);
  const totalUnreadItems = unreadAlerts.length + outboxCount;

  if (!isOpen) return null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/20 z-50 md:hidden"
        onClick={onClose}
      />
      
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-xl z-50 flex flex-col md:top-16 md:w-80 md:h-auto md:max-h-[calc(100vh-80px)] md:rounded-l-lg"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900">Alerts</h2>
            {totalUnreadItems > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMarkAllItemsRead}
                disabled={isMarkingAllRead}
                className="h-7 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              >
                <CheckCheck className="w-3 h-3 mr-1" />
                {isMarkingAllRead ? 'Marking...' : 'Mark all as read'}
              </Button>
            )}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content */}
        <ScrollArea className="flex-1 bg-white">
          {isLoading ? (
            <div className="p-4 space-y-4 bg-white">
              {Array(3).fill(0).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white">
              {/* Outbox messages */}
              {outboxCount > 0 && (
                <div>
                  <div className="px-4 py-2 bg-orange-50 border-b flex items-center justify-between">
                    <h3 className="text-sm font-medium text-orange-700 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      New Drafts ({outboxCount})
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDismissOutbox}
                      className="h-7 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-100"
                    >
                      <CheckCheck className="w-3 h-3 mr-1" />
                      Mark as read
                    </Button>
                  </div>
                  {outboxMessages.map((message) => (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border-b border-gray-100 last:border-b-0 bg-orange-50"
                    >
                      <Link
                        to={createPageUrl("Outbox")}
                        onClick={() => {
                          handleViewOutboxMessage(message.id);
                          onClose();
                        }}
                        className="block w-full p-4 text-left hover:bg-orange-100 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <Mail className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                          
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-gray-900">
                              {message.subject || 'New message ready'}
                            </h4>
                            <p className="text-xs text-gray-600 mt-1">
                              To: {message.person_name} at {message.account_name}
                            </p>
                            {message.scheduled_send_at && (
                              <p className="text-xs text-orange-600 mt-1">
                                Scheduled for {format(new Date(message.scheduled_send_at), 'MMM d, h:mm a')}
                              </p>
                            )}
                          </div>
                          
                          <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Unread alerts */}
              {unreadAlerts.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-gray-50 border-b">
                    <h3 className="text-sm font-medium text-gray-700">
                      Unread ({unreadAlerts.length})
                    </h3>
                  </div>
                  {unreadAlerts.map((alert) => (
                    <AlertItem
                      key={alert.id}
                      alert={alert}
                      externalUrl={alertUrls[alert.id]}
                      onRead={handleRead}
                    />
                  ))}
                </div>
              )}

              {/* Recent alerts */}
              {readAlerts.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-gray-50 border-b">
                    <h3 className="text-sm font-medium text-gray-700">Recent</h3>
                  </div>
                  {readAlerts.map((alert) => (
                    <AlertItem
                      key={alert.id}
                      alert={alert}
                      externalUrl={alertUrls[alert.id]}
                      onRead={handleRead}
                    />
                  ))}
                </div>
              )}

              {alerts.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  <div className="w-12 h-12 bg-gray-100 rounded-full mx-auto mb-3 flex items-center justify-center">
                    <UserCheck className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-sm">No alerts yet</p>
                  <p className="text-xs mt-1">You'll see notifications here when there's activity</p>
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <Link to={createPageUrl("AlertSettings")}>
            <Button variant="outline" size="sm" className="w-full" onClick={onClose}>
              Manage Alert Settings
            </Button>
          </Link>
        </div>
      </motion.div>
    </>
  );
}